/*
  FITONIQ BUNDLE BUILDER JS — Stable + Shopify rounding v10
  - No top-level await (prevents render break)
  - Gift by product HANDLE (auto added, excluded from min/max)
  - Defensive error handling
*/

document.addEventListener('DOMContentLoaded', () => {
  const section = document.querySelector('#fitoniq-bundle');
  if (!section) return;

  // ======== HORIZON / SHOPIFY DESIGN MODE SUPPORT ========
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target && event.target.id && event.target.id.includes('fitoniq-bundle')) {
        window.location.reload();
      }
    });
  }

  // ======== CONFIG AND STATE ========
  const itemsData = safeJSON(section.querySelector('[data-items]')?.textContent) || [];
  const cfg = safeJSON(section.querySelector('[data-config]')?.textContent) || {};
  const min = toInt(section.dataset.minItems, 0);
  const max = toInt(section.dataset.maxItems, 0);
  const discount = toInt(section.dataset.discountPercent, 0);
  const bundleTitle = section.dataset.bundleProductTitle || 'Fitoniq Pack';
  const bundleId = section.dataset.bundleProductId || '0';
  const allowRemove = section.dataset.allowRemove === 'true';

  const grid = section.querySelector('[data-grid]');
  const subtotalEl = section.querySelector('[data-subtotal]');
  const discountEl = section.querySelector('[data-discount]');
  const totalEl = section.querySelector('[data-total]');
  const messageEl = section.querySelector('[data-message] p');
  const messageTopEl = section.querySelector('[data-message-top] p');
  const progressFill = section.querySelector('[data-progress-fill]');
  const progressCount = section.querySelector('[data-progress-count]');
  const selectedList = section.querySelector('[data-selected-list]');
  const btnAdd = section.querySelector('[data-add-top]');
  const btnReset = section.querySelector('[data-reset]');

  const state = new Map();
  let giftProduct = null; // {handle, product_id, variant_id, title, image, price}

  // Small helpers
  function safeJSON(s) { try { return JSON.parse(s || ''); } catch { return null; } }
  function toInt(v, d=0){ const n = parseInt(v,10); return Number.isFinite(n)?n:d; }
  function euros(cents){ return `€${(cents/100).toFixed(2)}`; }

  // ======== RENDER GRID ========
  const renderGrid = () => {
    if (!grid) return;
    grid.innerHTML = '';

    itemsData.forEach(item => {
      const qty = state.get(item.variant_id) || 0;
      const isGift = giftProduct && item.product_id === giftProduct.product_id;

      const card = document.createElement('div');
      card.className = 'fitq-card';
      card.dataset.handle = item.handle;
      card.innerHTML = `
        <div class="fitq-card__img"><img src="${item.image}" alt="${escapeHtml(item.title)}"></div>
        <div class="fitq-card__meta">
          <div class="fitq-card__name">${escapeHtml(item.title)}${isGift ? ' 🎁' : ''}</div>
          <div class="fitq-card__price">${euros(item.price)}</div>
        </div>
        <div class="fitq-card__qty">
          <button class="fitq-btn" data-minus ${isGift ? 'disabled' : ''}>−</button>
          <div class="fitq-q" data-qty>${qty}</div>
          <button class="fitq-btn" data-plus ${isGift ? 'disabled' : ''}>+</button>
        </div>
      `;

      if (!isGift) {
        card.querySelector('[data-minus]').addEventListener('click', () => updateQty(item.variant_id, -1));
        card.querySelector('[data-plus]').addEventListener('click', () => updateQty(item.variant_id, 1));
      }
      grid.appendChild(card);
    });
  };

  // ======== UPDATE STATE ========
  const updateQty = (id, change) => {
    if (giftProduct && id === giftProduct.variant_id) return; // gift is fixed at 1

    const current = state.get(id) || 0;
    const total = getTotalSelected();
    let next = current + change;

    if (change > 0 && total >= max) {
      section.classList.add('fitq-shake');
      setTimeout(() => section.classList.remove('fitq-shake'), 400);
      setMessage(cfg.max_reached_message || '¡Pack completo — listo para comprar!');
      return;
    }
    if (next < 0) next = 0;

    state.set(id, next);
    renderGrid();
    updateSummary();
  };

  const getTotalSelected = () => {
    let total = 0;
    for (const [variantId, q] of state.entries()) {
      if (giftProduct && variantId === giftProduct.variant_id) continue; // exclude gift from count
      total += q;
    }
    return total;
  };

  // ======== UPDATE SUMMARY ========
  const updateSummary = () => {
    let subtotal = 0;
    const totalItems = getTotalSelected();

    if (selectedList) selectedList.innerHTML = '';

    // Regular items list + subtotal
    itemsData.forEach(item => {
      const q = state.get(item.variant_id) || 0;
      if (q > 0) {
        subtotal += item.price * q;

        if (selectedList) {
          const li = document.createElement('li');
          li.className = 'fitq-selected__item';
          li.innerHTML = `
            <div class="fitq-selected__left">
              <div class="fitq-selected__thumb"><img src="${item.image}" alt="${escapeHtml(item.title)}"></div>
              <div class="fitq-selected__name"><strong>${q}×</strong> ${escapeHtml(item.title)}</div>
            </div>
            ${allowRemove ? `<button class="fitq-remove" data-remove="${item.variant_id}" aria-label="Eliminar">×</button>` : ''}
          `;
          selectedList.appendChild(li);
        }
      }
    });

    // Gift appears at the end of the list
    if (giftProduct && cfg.gift_enabled && selectedList) {
      const li = document.createElement('li');
      li.className = 'fitq-selected__item fitq-selected__gift';
      li.innerHTML = `
        <div class="fitq-selected__left">
          <div class="fitq-selected__thumb">
            ${giftProduct.image ? `<img src="${giftProduct.image}" alt="${escapeHtml(giftProduct.title)}">` : '<span>🎁</span>'}
          </div>
          <div class="fitq-selected__name">${escapeHtml(giftProduct.title)} 🎁</div>
        </div>
      `;
      selectedList.appendChild(li);
    }

    // Totals with Shopify-style rounding (ceil per unit after discount)
    const ROUNDING_MODE = 'ceil_unit';
    let totalAfterDiscount = 0;

    if (ROUNDING_MODE === 'ceil_unit') {
      itemsData.forEach(item => {
        const q = state.get(item.variant_id) || 0;
        if (q > 0) {
          const unit = item.price / 100;
          const discountedUnit = Math.ceil(unit * (1 - discount / 100) * 100) / 100;
          totalAfterDiscount += discountedUnit * q;
        }
      });
    } else {
      // Fallback if ever needed
      itemsData.forEach(item => {
        const q = state.get(item.variant_id) || 0;
        if (q > 0) {
          const line = (item.price / 100) * q * (1 - discount / 100);
          totalAfterDiscount += Math.round(line * 100) / 100;
        }
      });
    }

    const totalCents = Math.round(totalAfterDiscount * 100);
    const discountAmt = subtotal - totalCents;

    // UI updates
    if (subtotalEl) {
      subtotalEl.textContent = euros(subtotal);
      const subtotalRow = subtotalEl.closest('.fitq-summary__row');
      if (discount > 0 && totalItems > 0) subtotalRow.classList.add('fitq-summary__row--strike');
      else subtotalRow.classList.remove('fitq-summary__row--strike');
    }
    if (discountEl) discountEl.textContent = discount > 0 ? euros(discountAmt) : '€0.00';
    if (totalEl) totalEl.textContent = euros(totalCents);

    if (progressFill && progressCount) {
      const pct = Math.min((totalItems / max) * 100, 100);
      progressFill.style.width = pct + '%';
      progressCount.textContent = `${totalItems}`;
    }

// ======== Dynamic remaining products message ========
const remaining = Math.max(min - totalItems, 0);

if (totalItems < min) {
  const msg = `Selecciona ${remaining} producto${remaining === 1 ? '' : 's'} más para completar tu pack.`;
  setMessage(msg);
} else if (totalItems >= max) {
  setMessage('Pack completo - listo para comprar');
} else {
  const msg = `Puedes añadir ${max - totalItems} producto${max - totalItems === 1 ? '' : 's'} más a tu pack.`;
  setMessage(msg);
}

if (btnAdd) btnAdd.disabled = totalItems < min;
  };

  function setMessage(msg) {
    if (messageEl) messageEl.textContent = msg;
    if (messageTopEl) messageTopEl.textContent = msg;
  }

  // Sidebar remove (event delegation)
  if (selectedList) {
    selectedList.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-remove]');
      if (!btn) return;
      const vid = parseInt(btn.getAttribute('data-remove'), 10);
      if (Number.isFinite(vid)) {
        state.set(vid, 0);
        renderGrid();
        updateSummary();
      }
    });
  }

  // ======== ADD TO CART ========
  const addToCart = async () => {
    let units = 0;
    const queue = [];
    const summary = [];

    itemsData.forEach(item => {
      const q = state.get(item.variant_id) || 0;
      if (q > 0) {
        if (!giftProduct || item.product_id !== giftProduct.product_id) units += q; // exclude gift
        queue.push({ id: item.variant_id, quantity: q });
        summary.push(`${item.title} × ${q}`);
      }
    });

    if (giftProduct && cfg.gift_enabled) {
      queue.push({ id: giftProduct.variant_id, quantity: 1 });
      summary.push(`${giftProduct.title} × 1`);
    }

    if (units < min) {
      alert((cfg.min_message || 'Selecciona al menos [[min]] productos')
        .replace('[[min]]', min).replace('[[max]]', max));
      return;
    }

    try {
      if (btnAdd) { btnAdd.disabled = true; btnAdd.classList.add('is-loading'); }

      // Add all lines sequentially
      for (const line of queue) {
        // eslint-disable-next-line no-await-in-loop
        await fetch(window.Shopify?.routes?.root ? `${Shopify.routes.root}cart/add.js` : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            id: line.id,
            quantity: line.quantity,
            properties: { "_Bundle": bundleTitle, "_Bundle ID": bundleId, "_Bundle Items": summary.join(', ') }
          })
        });
      }

      // Apply discount code if present
      const randomCode = (section.dataset.randomCode || '').trim();
      if (randomCode) {
        try { await fetch(`/discount/${randomCode}`, { credentials: 'include' }); } catch {}
      }

      window.location.href = '/cart';
    } catch (err) {
      console.error('Cart add failed', err);
      alert('No se pudo añadir el pack al carrito. Inténtalo de nuevo.');
      if (btnAdd) { btnAdd.disabled = false; btnAdd.classList.remove('is-loading'); }
    }
  };

  const resetBundle = () => {
    state.clear();
    if (giftProduct && cfg.gift_enabled) state.set(giftProduct.variant_id, 1);
    renderGrid();
    updateSummary();
  };

  if (btnAdd) btnAdd.addEventListener('click', addToCart);
  if (btnReset) btnReset.addEventListener('click', resetBundle);

  // ======== GIFT INIT (by HANDLE) — no top-level await ========
  function initGiftByHandle() {
    const giftHandle = (cfg.gift_handle || '').trim();
    if (!cfg.gift_enabled || !giftHandle) return Promise.resolve();

    return fetch(`/products/${giftHandle}.js`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Gift not found')))
      .then(data => {
        const v = (data && data.variants && data.variants[0]) ? data.variants[0] : null;
        if (!v) return;

        giftProduct = {
          handle: data.handle,
          product_id: data.id,
          variant_id: v.id,
          title: data.title,
          image: (data.images && data.images[0]) ? data.images[0] : '',
          price: v.price || 0
        };
        // Preselect gift (excluded from min/max elsewhere)
        state.set(v.id, 1);
      })
      .catch(err => {
        console.warn('Gift fetch failed:', err && err.message ? err.message : err);
      });
  }

  // ======== Utilities ========
  function escapeHtml(str){
    return String(str || '')
      .replace(/&/g,'&amp;').replace(/</g,'&lt;')
      .replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#039;');
  }

  // ======== INIT (render first, then init gift asynchronously) ========
  renderGrid();
  updateSummary();

  initGiftByHandle().then(() => {
    // After gift arrives, refresh UI to display it (and lock its qty)
    renderGrid();
    updateSummary();
  });

// ======== QUICK VIEW (Horizon-compatible) ========
const modal = document.getElementById('fitq-modal');
const modalBody = modal?.querySelector('[data-modal-body]');
const closeEls = modal?.querySelectorAll('[data-close]');

// Delegate click from the product grid
if (grid && modal) {
  grid.addEventListener('click', async (e) => {
    const card = e.target.closest('.fitq-card');
    if (!card) return;

    // Only trigger if click on image or name
    if (!e.target.closest('.fitq-card__img') && !e.target.closest('.fitq-card__name')) return;

    const handle = card.dataset.handle;
    if (!handle) return;

    modal.classList.add('is-visible');
    document.body.classList.add('modal-open');
    modalBody.innerHTML = '<div class="fitq-loading">Cargando...</div>';

    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) throw new Error('No encontrado');
      const product = await res.json();

      modalBody.innerHTML = `
        <img src="${product.images[0] || ''}" alt="${product.title}">
        <h2>${product.title}</h2>
        <p style="color:#555;margin-top:6px;white-space:pre-line;">
          ${(product.description || '').slice(0, 400)}
        </p>
        <p style="font-weight:700;margin-top:12px">€${(product.price / 100).toFixed(2)}</p>
        <div style="margin-top:20px;text-align:right">
          <a href="/products/${product.handle}" target="_blank" rel="noopener noreferrer" class="fitq-modal__link">
            Ver producto completo →
          </a>
        </div>
      `;
    } catch (err) {
      console.warn('QuickView error:', err);
      modalBody.innerHTML = '<p>Error al cargar el producto.</p>';
    }
  });
}

// Close modal
if (closeEls) {
  closeEls.forEach(el => el.addEventListener('click', () => {
    modal.classList.remove('is-visible');
    document.body.classList.remove('modal-open');
  }));
}

});
